import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useMemo } from 'react'

import CompanyLogoOrName from '@/components/misc/CompanyLogoOrName'
import { useShouldShowAdditionalCompanyInfo } from '@/components/misc/CompanyProfile/hooks/useShouldShowAdditionalCompanyInfo'
import ExternalLink from '@/components/misc/ExternalLink'
import RqCertifiedLogo from '@/components/misc/RqCertifiedLogo/RqCertifiedLogo'
import { Heading } from '@/components/ui/heading'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { COMPANY_PROFILE_IDENTIFIER_TYPE } from '@/lib/constants'
import { faLocationDot } from '@/lib/fontAwesomePro/light'
import { buildDebugTooltip } from '@/lib/helpers/helperFunctions'
import { ROUTES } from '@/lib/routes'
import { CompanyProfileItem } from '@/types/responses/companies'

interface Props {
  companyProfile: CompanyProfileItem
}

const CompanyInfo: React.FC<Props> = ({ companyProfile }) => {
  const isRqRated = companyProfile?.company?.isRqRated
  const { directory, name, company } = companyProfile
  const shouldShowAdditionalCompanyInfo = useShouldShowAdditionalCompanyInfo()
  const shouldShowCompanyName =
    shouldShowAdditionalCompanyInfo || !company?.logoUrl

  const mainAddress = useMemo(() => {
    if (
      directory &&
      !directory.fca?.frnTradingNameId &&
      directory.postcode &&
      directory.county
    ) {
      return { postcode: directory.postcode, county: directory.county }
    }

    if (company) {
      for (const address of company.addresses) {
        if (address.isMainAddress) {
          return { postcode: address.postcode, county: address.city }
        }
      }
    }

    return null
  }, [directory, company])

  return (
    <>
      <div className="flex flex-row flex-wrap items-center gap-2">
        {/* Logo */}
        <Tooltip>
          {/* Need the div wrapper for the tooltip to render properly */}
          <div
            title={buildDebugTooltip({
              panelId: companyProfile.panel?.id,
              companyId: companyProfile?.company?.id,
              sendingReferralCode:
                companyProfile?.metadata?.sendingReferralCode,
            })}
          >
            <TooltipTrigger asChild>
              <CompanyLogoOrName
                name={companyProfile.name}
                logoUrl={company?.logoUrl}
                wrapperProps={{ className: 'flex pr-4' }}
                imageStyle={{ maxHeight: '55px', maxWidth: '350px' }}
                companyTypeIconStyle={{
                  width: '34px',
                  height: '34px',
                  maxHeight: '34px',
                }}
                showNameFallback={false}
              />
            </TooltipTrigger>
          </div>

          <TooltipContent>{name}</TooltipContent>
        </Tooltip>

        {/* Company name & address */}
        <section
          className="flex grow flex-row items-center gap-6 sm:justify-between"
        >
          <div className="flex flex-col gap-1">
            {shouldShowCompanyName && (
              <Heading size={2} className="mb-0">
                {companyProfile.name}
              </Heading>
            )}

            {shouldShowAdditionalCompanyInfo && (
              <div className="flex flex-col gap-1">
                {/* Trading name */}
                {directory && directory.fca?.frnTradingNameId && (
                  <div className="text-md text-dim mr-4 inline-block">
                    Trading name of{' '}
                    <ExternalLink
                      href={ROUTES.companyProfile({
                        searchParams: {
                          identifierType: COMPANY_PROFILE_IDENTIFIER_TYPE.frn,
                          identifier: directory.fca?.frn,
                        },
                      })}
                      className="text-inherit underline"
                    >
                      {directory.fca?.name}
                    </ExternalLink>
                  </div>
                )}

                {mainAddress && (
                  <div className="text-md inline-block">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="mr-2"
                      size="lg"
                    />
                    {mainAddress.county}, {mainAddress.postcode}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RQ Certified */}
          <div>
            {isRqRated && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <RqCertifiedLogo
                    style={{ maxWidth: '60px' }}
                    title="RQ Certified"
                  />
                </TooltipTrigger>

                <TooltipContent>
                  <strong>{name}</strong> is RQ Certified
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </section>
      </div>
    </>
  )
}

export default CompanyInfo
